import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineM } from '@sorare/core/src/atoms/typography';
import { FOOTBALL_PLAY_PRO_FIXTURE_COMPETITION_CONTENDER_CONTENDER } from '@sorare/core/src/constants/__generated__/routes';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { fantasy, rivals } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { Color } from '@sorare/core/src/style/types';
import { regularMaterial } from '@sorare/core/src/style/utils';

import {
  getComposeTeamRoute,
  isFixtureLive,
  isFixtureOpened,
} from '@sorare/gameplay/src/lib/so5';

import { NextAction_so5Campaign } from './__generated__/index.graphql';

const Root = styled(SBHorizontal)`
  width: calc(100vw - var(--quadruple-unit));
  margin: auto;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  ${regularMaterial}

  & > * {
    flex: 1;
  }

  @media ${laptopAndAbove} {
    width: 100%;
    flex-direction: column;
    padding: var(--triple-unit);
    border-radius: var(--double-unit);

    & > * {
      flex: unset;
    }
  }
`;
const Content = styled(Vertical)`
  @media ${laptopAndAbove} {
    align-items: center;
  }
`;
const Title = styled.p`
  font: var(--t-body-m);
  @media ${laptopAndAbove} {
    font: var(--t-headline-m);
  }
`;
const Caption = styled.p<{ color?: Color }>`
  ${({ color }) => color && `color: ${color};`}
  font: var(--t-body-s);
  @media ${laptopAndAbove} {
    font: var(--t-body-m);
  }
`;

type Props = { so5Campaign: NextAction_so5Campaign; composeTeamUrl?: string };

export const NextAction = ({ so5Campaign, composeTeamUrl }: Props) => {
  const { so5Leaderboards } = so5Campaign || {};

  if (!so5Leaderboards?.length) {
    return null;
  }

  const liveSo5Leaderboard = so5Leaderboards.find(({ so5Fixture }) =>
    isFixtureLive(so5Fixture)
  );
  const liveContender = liveSo5Leaderboard?.mySo5LeaderboardContenders.find(
    contender => contender.so5Lineup
  );

  const nextUpcomingSo5Leaderboard = so5Leaderboards.find(({ so5Fixture }) =>
    isFixtureOpened(so5Fixture)
  );
  const nextUpcomingContender =
    nextUpcomingSo5Leaderboard?.mySo5LeaderboardContenders.find(
      contender => contender.so5Lineup
    );

  if (
    so5Leaderboards.every(
      so5Leaderboard => so5Leaderboard.so5Fixture.aasmState === 'preparing'
    )
  ) {
    return null;
  }

  if (liveSo5Leaderboard || nextUpcomingSo5Leaderboard) {
    const actualLeaderboard = nextUpcomingSo5Leaderboard || liveSo5Leaderboard;
    const displayingLive = actualLeaderboard?.slug === liveSo5Leaderboard?.slug;
    const actualContender = displayingLive
      ? liveContender
      : nextUpcomingContender;

    if (!actualLeaderboard) {
      return null;
    }

    const composeTeamRoute =
      composeTeamUrl ||
      getComposeTeamRoute({
        leaderboardSlug: actualLeaderboard.slug,
        lineupId: actualContender?.so5Lineup?.id,
        sport: Sport.FOOTBALL,
        eventType: 'classic',
      });

    return (
      <Root gap={2.5}>
        <Content gap={0}>
          <Title as="p">{actualLeaderboard.so5Fixture.displayName}</Title>
          {actualContender?.so5Lineup ? (
            <Caption color="var(--c-rivals-win)">
              <FormattedMessage {...fantasy.entered} />
            </Caption>
          ) : (
            <Caption color="var(--c-yellow-400)">
              <FormattedMessage {...fantasy.notEntered} />
            </Caption>
          )}
        </Content>
        {actualContender?.so5Lineup ? (
          <Button
            size="medium"
            color={displayingLive ? 'tertiary' : 'secondary'}
            fullWidth
            to={
              displayingLive && !nextUpcomingSo5Leaderboard
                ? generatePath(
                    FOOTBALL_PLAY_PRO_FIXTURE_COMPETITION_CONTENDER_CONTENDER,
                    {
                      fixture: actualLeaderboard.so5Fixture.slug,
                      contender: actualContender.slug,
                      competition: actualLeaderboard.so5League.slug,
                    }
                  )
                : composeTeamRoute
            }
          >
            <FormattedMessage
              {...(displayingLive ? fantasy.viewLineup : rivals.editLineup)}
            />
          </Button>
        ) : (
          !displayingLive && (
            <Button
              size="medium"
              color="primary"
              fullWidth
              to={composeTeamRoute}
            >
              <FormattedMessage {...fantasy.setLineup} />
            </Button>
          )
        )}
      </Root>
    );
  }

  const experiences = so5Leaderboards
    .flatMap(so5Leaderboard =>
      so5Leaderboard.mySo5LeaderboardContenders.flatMap(contender =>
        contender.so5Lineup?.so5Rankings
          .at(0)
          ?.eligibleRewards.flatMap(reward => reward.experiences)
      )
    )
    .filter(Boolean)
    .filter(({ title }) => title);

  const experiencesByTitle = groupBy(
    experiences,
    experience => experience.title!
  );

  const experienceString = Object.entries(experiencesByTitle)
    .map(([title, exps]) => `${(exps || []).length}x ${title}`)
    .join(', ');

  return (
    <Root>
      <Content gap={0}>
        <HeadlineM as="p">
          {experiences.length ? (
            <FormattedMessage
              id="campaigns.NextAction.youHaveRewards"
              defaultMessage="You have {rewards}"
              values={{ rewards: experienceString }}
            />
          ) : (
            <FormattedMessage
              id="campaigns.NextAction.youHaveNoRewards"
              defaultMessage="You have no rewards"
            />
          )}
        </HeadlineM>
        <BodyM as="p" color="var(--c-nd-600)">
          <FormattedMessage
            id="campaigns.NextAction.drawWillBeAfterGw"
            defaultMessage="Draw will be after {gameWeek}"
            values={{
              gameWeek:
                so5Leaderboards[so5Leaderboards.length - 1]?.so5Fixture
                  .displayName,
            }}
          />
        </BodyM>
      </Content>
    </Root>
  );
};

NextAction.fragments = {
  so5Campaign: gql`
    fragment NextAction_so5Campaign on So5Campaign {
      slug
      so5Leaderboards {
        slug
        so5League {
          slug
        }
        mySo5LeaderboardContenders {
          slug
          so5Lineup {
            id
            so5Rankings {
              id
              eligibleRewards {
                experiences {
                  title
                }
              }
            }
          }
        }
        so5Fixture {
          slug
          displayName
          ...isFixtureOpened_so5Fixture
          ...isFixtureLive_so5Fixture
        }
      }
    }
    ${isFixtureOpened.fragments.so5Fixture}
    ${isFixtureLive.fragments.so5Fixture}
  ` as TypedDocumentNode<NextAction_so5Campaign>,
};
